import { openThreadReplicaDb, requestToPromise, transactionToPromise } from "@/lib/thread-replica-idb"
import {
  buildEmptyThreadReplicaMeta,
  THREAD_REPLICA_META_STORE,
  type ThreadReplicaMetaRecord,
  type ThreadReplicaScope,
  normalizeThreadReplicaMetaRecord,
} from "@/lib/thread-replica-types"

function buildThreadReplicaMetaKey(args: ThreadReplicaScope): [string, string] {
  return [args.scopeKey, args.sessionId]
}

function normalizeHistoryToken(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export async function readThreadReplicaMeta(
  args: ThreadReplicaScope,
): Promise<ThreadReplicaMetaRecord | null> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return null
  }

  const transaction = db.transaction(THREAD_REPLICA_META_STORE, "readonly")
  const meta = normalizeThreadReplicaMetaRecord(
    await requestToPromise(transaction.objectStore(THREAD_REPLICA_META_STORE).get(buildThreadReplicaMetaKey(args))),
  )
  await transactionToPromise(transaction)
  return meta
}

export async function writeThreadReplicaMeta(args: {
  meta: ThreadReplicaMetaRecord
}): Promise<boolean> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return false
  }

  const transaction = db.transaction(THREAD_REPLICA_META_STORE, "readwrite")
  transaction.objectStore(THREAD_REPLICA_META_STORE).put(args.meta)
  await transactionToPromise(transaction)
  return true
}

export async function advanceThreadReplicaRuntimeCursorMeta(args: ThreadReplicaScope & {
  nextCursor: number
  historyToken?: string | null
  expectedEpoch?: number
  nowMs: number
}): Promise<
  | { status: "advanced" | "noop"; meta: ThreadReplicaMetaRecord }
  | { status: "stale_writer" | "storage_unavailable" }
> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return { status: "storage_unavailable" }
  }

  const transaction = db.transaction(THREAD_REPLICA_META_STORE, "readwrite")
  const store = transaction.objectStore(THREAD_REPLICA_META_STORE)
  const currentMeta =
    normalizeThreadReplicaMetaRecord(
      await requestToPromise(store.get(buildThreadReplicaMetaKey(args))),
    ) ?? buildEmptyThreadReplicaMeta(args)

  if (
    typeof args.expectedEpoch === "number" &&
    Number.isFinite(args.expectedEpoch) &&
    Math.floor(args.expectedEpoch) !== currentMeta.replicaEpoch
  ) {
    transaction.abort()
    return { status: "stale_writer" }
  }

  const nextCursor = Math.max(0, Math.floor(args.nextCursor))
  const normalizedHistoryToken = normalizeHistoryToken(args.historyToken)
  const shouldAdvanceCursor = nextCursor > currentMeta.localCursor
  const canPromoteValidation =
    normalizedHistoryToken !== null &&
    nextCursor === currentMeta.localCursor && (
      nextCursor === 0 ||
      currentMeta.lastCheckpointCursor >= nextCursor
    )
  const shouldPromoteValidation =
    canPromoteValidation && (
      currentMeta.historyToken !== normalizedHistoryToken ||
      currentMeta.lastValidatedAtMs === null
    )

  if (!shouldAdvanceCursor && !shouldPromoteValidation) {
    transaction.abort()
    return { status: "noop", meta: currentMeta }
  }

  const nextMeta: ThreadReplicaMetaRecord = {
    ...currentMeta,
    historyToken:
      shouldPromoteValidation
        ? normalizedHistoryToken
        // A cursor-only advance can outrun the currently checkpointed runtime.
        // Clear validation until a checkpoint anchored at the new cursor is
        // saved; otherwise persisted summaries could incorrectly treat the
        // newer cursor as a continuity-validated resume point.
        : shouldAdvanceCursor
          ? null
          : currentMeta.historyToken,
    localCursor: shouldAdvanceCursor ? nextCursor : currentMeta.localCursor,
    coverage:
      nextCursor > 0
        ? currentMeta.coverage === "tail_window"
          ? "tail_window"
          : "full"
        : currentMeta.coverage,
    lastUpdatedAtMs: Math.max(0, Math.floor(args.nowMs)),
    lastValidatedAtMs:
      shouldPromoteValidation
        ? Math.max(0, Math.floor(args.nowMs))
        : currentMeta.lastValidatedAtMs,
    lastResetReason: null,
  }
  store.put(nextMeta)
  await transactionToPromise(transaction)
  return { status: "advanced", meta: nextMeta }
}

export function normalizeThreadReplicaMetaFromStoreValue(value: unknown): ThreadReplicaMetaRecord | null {
  return normalizeThreadReplicaMetaRecord(value)
}
